import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Building, 
  User,
  Mail, 
  Phone,
  MapPin,
  Users,
  Calendar,
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  UserPlus,
  Building2,
  Clock,
  CreditCard,
  FileText
} from 'lucide-react';
import apiService from '../services/api';

const MemberProfile = ({ member, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedMember, setEditedMember] = useState(member);
  const [assignedOffice, setAssignedOffice] = useState(null);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    email: ''
  });

  useEffect(() => {
    loadRelatedData();
  }, [member.id]);

  const loadRelatedData = async () => {
    setLoading(true);
    try {
      // Load offices to find assigned office
      const officesData = await apiService.getOffices();
      setOffices(officesData);
      
      // Find assigned office for this member
      if (member.office_id) {
        const assigned = officesData.find(office => office.id === member.office_id);
        setAssignedOffice(assigned);
      }
    } catch (error) {
      console.error('Error loading related data:', error);
      setError('Failed to load related information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await apiService.updateMember(member.id, editedMember);
      setIsEditing(false);
      if (onUpdate) onUpdate(editedMember);
    } catch (error) {
      console.error('Error updating member:', error);
      setError('Failed to update member');
    }
  };

  const handleCancel = () => {
    setEditedMember(member);
    setIsEditing(false);
  };

  const handleAddEmployee = async () => {
    try {
      await apiService.addEmployeeToMember(member.id, newEmployee);
      setNewEmployee({ name: '', role: '', email: '' });
      setShowAddEmployee(false);
      // Reload member data
      const updatedMember = await apiService.getMember(member.id);
      if (onUpdate) onUpdate(updatedMember);
    } catch (error) {
      console.error('Error adding employee:', error);
      setError('Failed to add employee');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Prospect':
        return 'bg-yellow-100 text-yellow-800';
      case 'Member':
        return 'bg-green-100 text-green-800';
      case 'Former Member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMembershipTypeColor = (type) => {
    switch (type) {
      case 'Premium':
        return 'bg-purple-100 text-purple-800';
      case 'Standard':
        return 'bg-blue-100 text-blue-800';
      case 'Basic':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-start p-6 border-b border-gray-200">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <Building className="w-8 h-8 text-green-600" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">{member.company_name}</h2>
              <Badge className={getMembershipTypeColor(member.membership_type)}>
                {member.membership_type}
              </Badge>
            </div>
            
            <p className="text-gray-600 mb-1">{member.contact_name || 'No Primary Contact'}</p>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{member.location_name}</span>
              </div>
              
              {member.contact_email && (
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-1" />
                  <a href={`mailto:${member.contact_email}`} className="text-blue-600 hover:text-blue-700">
                    {member.contact_email}
                  </a>
                </div>
              )}
              
              {member.contact_phone && (
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-1" />
                  <a href={`tel:${member.contact_phone}`} className="text-blue-600 hover:text-blue-700">
                    {member.contact_phone}
                  </a>
                </div>
              )}
              
              <div className="flex items-center">
                <Users className="w-4 h-4 mr-1" />
                <span>{member.employees?.length || 0} employees</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button onClick={handleSave} size="sm">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button onClick={handleCancel} variant="outline" size="sm">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="employees">
              <Users className="w-4 h-4 mr-2" />
              Employees ({member.employees?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="office">
              <Building2 className="w-4 h-4 mr-2" />
              Office Assignment
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Company Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div>
                        <Label htmlFor="company_name">Company Name</Label>
                        <Input
                          id="company_name"
                          value={editedMember.company_name || ''}
                          onChange={(e) => setEditedMember({...editedMember, company_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="membership_type">Membership Type</Label>
                        <select
                          id="membership_type"
                          className="w-full p-2 border border-gray-300 rounded-md"
                          value={editedMember.membership_type || ''}
                          onChange={(e) => setEditedMember({...editedMember, membership_type: e.target.value})}
                        >
                          <option value="Basic">Basic</option>
                          <option value="Standard">Standard</option>
                          <option value="Premium">Premium</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Company:</span>
                        <span className="font-medium">{member.company_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Membership:</span>
                        <Badge className={getMembershipTypeColor(member.membership_type)}>
                          {member.membership_type}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium">{member.location_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Join Date:</span>
                        <span className="font-medium">{formatDate(member.created_at)}</span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Primary Contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Primary Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing ? (
                    <>
                      <div>
                        <Label htmlFor="contact_name">Contact Name</Label>
                        <Input
                          id="contact_name"
                          value={editedMember.contact_name || ''}
                          onChange={(e) => setEditedMember({...editedMember, contact_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Email</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={editedMember.contact_email || ''}
                          onChange={(e) => setEditedMember({...editedMember, contact_email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone">Phone</Label>
                        <Input
                          id="contact_phone"
                          value={editedMember.contact_phone || ''}
                          onChange={(e) => setEditedMember({...editedMember, contact_phone: e.target.value})}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-medium">{member.contact_name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">
                          {member.contact_email ? (
                            <a href={`mailto:${member.contact_email}`} className="text-blue-600 hover:text-blue-700">
                              {member.contact_email}
                            </a>
                          ) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">
                          {member.contact_phone ? (
                            <a href={`tel:${member.contact_phone}`} className="text-blue-600 hover:text-blue-700">
                              {member.contact_phone}
                            </a>
                          ) : 'N/A'}
                        </span>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Team Members</h3>
              <Button onClick={() => setShowAddEmployee(true)} className="bg-blue-600 hover:bg-blue-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </div>

            {/* Add Employee Form */}
            {showAddEmployee && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Add New Employee</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="emp_name">Employee Name</Label>
                    <Input
                      id="emp_name"
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                      placeholder="Enter employee name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="emp_role">Role</Label>
                    <Input
                      id="emp_role"
                      value={newEmployee.role}
                      onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}
                      placeholder="Enter role/title"
                    />
                  </div>
                  <div>
                    <Label htmlFor="emp_email">Email Address</Label>
                    <Input
                      id="emp_email"
                      type="email"
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleAddEmployee} className="flex-1">
                      Add Employee
                    </Button>
                    <Button onClick={() => setShowAddEmployee(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Employees List */}
            <div className="space-y-4">
              {member.employees && member.employees.length > 0 ? (
                member.employees.map((employee, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{employee.name}</p>
                            <p className="text-sm text-gray-600">{employee.role}</p>
                            {employee.email && (
                              <p className="text-sm text-blue-600">
                                <a href={`mailto:${employee.email}`} className="hover:text-blue-700">
                                  {employee.email}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No employees added yet</p>
                  <p className="text-sm text-gray-400">Add team members to track who works for this company</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Office Assignment Tab */}
          <TabsContent value="office" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Office Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                {assignedOffice ? (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 mr-2 text-emerald-600" />
                        <span className="font-medium text-emerald-700">Assigned Office</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-lg font-semibold text-emerald-800">
                        {assignedOffice.type} {assignedOffice.office_number}
                      </p>
                      <div className="text-sm text-emerald-600">
                        <div className="flex items-center mb-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          <span>{assignedOffice.location_name}</span>
                        </div>
                        <div className="flex items-center mb-1">
                          <Users className="w-3 h-3 mr-1" />
                          <span>{assignedOffice.desks} desks • Floor {assignedOffice.floor}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          <span>Status: {assignedOffice.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">No office assigned</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Use the Office Management section to assign this company to an office
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MemberProfile;